import {
  FieldTimeRangePicker,
  FlexBox,
  IconButton,
  Text,
} from '@dixa/lib-shared-habla';
import type { OpeningHours } from 'lib-shared-types-api';
import moment from 'moment-timezone';
import { useCallback, useState } from 'react';
import { usePrevious, usePreviousDistinct, useUpdateEffect } from 'react-use';

import { defaultTime } from 'utils/date';

import styles from './intervalsSelect.module.scss';
import { getNewIntervals } from './utils/intervals';

export type IntervalsSelectProps = {
  errors: (null | string)[];
  isClosed: boolean;
  onChange: (intervals: OpeningHours['intervals']) => void;
  intervals: OpeningHours['intervals'];
  validate: (intervals: OpeningHours['intervals']) => void;
};

const maxLengthOfIntervals = 10;

export const IntervalsSelect = ({
  errors,
  isClosed,
  intervals: propIntervals,
  onChange,
  validate,
}: IntervalsSelectProps): JSX.Element => {
  const timeFormat = moment.localeData().longDateFormat('LT');

  const [intervals, setIntervals] = useState(propIntervals);

  const previousIntervals = usePreviousDistinct(intervals);
  const previousIsClosed = usePrevious(isClosed);

  useUpdateEffect(() => void onChange(intervals), [intervals]);

  const handleTimeChange = useCallback(
    (index: number) =>
      ({
        startTime: start,
        endTime: end,
      }: Record<'startTime' | 'endTime', string>): void => {
        setIntervals((prevIntervals) => {
          const newIntervals = [...prevIntervals];
          newIntervals[index] = { start, end };

          if (newIntervals.length > 1) {
            validate(newIntervals);
          }

          return newIntervals;
        });
      },
    [validate],
  );

  const handleAddNewIntervals = useCallback(() => {
    setIntervals((prevIntervals) => {
      const newIntervals = getNewIntervals(
        prevIntervals[prevIntervals.length - 1],
      );
      const newStateIntervals = [...prevIntervals, newIntervals];

      validate(newStateIntervals);

      return newStateIntervals;
    });
  }, [validate]);

  useUpdateEffect(() => {
    if (previousIsClosed !== isClosed) {
      const newIntervals = isClosed ? [] : previousIntervals || [defaultTime];
      if (isClosed || newIntervals.length > 1) {
        validate(newIntervals);
      }
      setIntervals(newIntervals);
    }
  }, [isClosed, previousIntervals, previousIsClosed]);

  const handleDeleteInterval = useCallback(
    (index: number) => () => {
      setIntervals((prevIntervals) => {
        const newIntervals = [...prevIntervals];
        newIntervals.splice(index, 1);

        validate(newIntervals);

        return newIntervals;
      });
    },
    [validate],
  );

  return intervals?.length && !isClosed ? (
    <>
      {intervals.map(({ start: startTime, end: endTime }, index) => (
        <FlexBox key={`name-${index}`} alignItems="flex-start">
          <FieldTimeRangePicker
            allowCustomTimeInput
            className={styles.timeRangePicker}
            dropdownProps={{ fixed: true }}
            input={{
              name: 'opening-hours',
              onChange: handleTimeChange(index),
              value: { startTime, endTime },
            }}
            meta={{
              error: errors[index],
            }}
            noLabel
            overrides={[{ hour: 23, minute: 59 }]}
            timeFormat={timeFormat}
          />
          {index === 0 ? (
            <IconButton
              className={styles.icon}
              disabled={intervals?.length >= maxLengthOfIntervals}
              icon="plus"
              onClick={handleAddNewIntervals}
              variant="tertiary"
            />
          ) : (
            <IconButton
              className={styles.icon}
              icon="trash"
              variant="tertiary"
              onClick={handleDeleteInterval(index)}
            />
          )}
        </FlexBox>
      ))}
    </>
  ) : (
    <Text.Paragraph className={styles.closed}>Closed</Text.Paragraph>
  );
};
